
import requestAPI from '@/logic/requestBeApi';
import router from '@/router';
import store from '@/store';
import helpers from '@/logic/helpers/forms/onboardingHelpers';
import { refreshTokenHandler } from '@/logic/auth';
import PhoneNumberInput from '@/components/Inputs/PhoneNumberInput.vue';
import CountryInput from '@/components/Inputs/CountryInput.vue';
import inputValidators from '@/logic/helpers/inputs/validators';
import VatNumberInput from '@/components/Inputs/VatNumberInput.vue';
import ServerResponseAlert from '@/components/ServerResponseAlert.vue';
import passDataToResponseAlert from '@/logic/helpers/api/passDataToResponseAlert';

export default {
  name: 'AssessorOnboardingCD',

  components: {
    PhoneNumberInput,
    CountryInput,
    VatNumberInput,
    ServerResponseAlert,
  },

  props: {
    isPartOfProfile: Boolean,
    company: {
      type: Object,
      default: (): Record<string, never> => ({}),
    },
    focusOnProgressCircle: Boolean,
  },

  data(): Record<string, unknown> {
    return {
      onboardingCD: {
        companyName: '',
        street: '',
        houseNumber: '',
        apartmentNumber: '',
        city: '',
        postalCode: '',
        country: '',
        vatNumber: '',
        email: '',
        bankName: '',
        bankNumber: '',
        bankSwift: '',
        registrationNumber: '',
        phone: '',
      },
      validationRules: {
        companyName: [{ validator: inputValidators.primitive, trigger: 'change' }],
        street: [{ validator: inputValidators.primitive, trigger: 'change' }],
        houseNumber: [{ validator: inputValidators.primitive, trigger: 'change' }],
        city: [{ validator: inputValidators.primitive, trigger: 'change' }],
        postalCode: [{ validator: inputValidators.primitive, trigger: 'change' }],
        country: [{ validator: inputValidators.primitive, trigger: 'change' }],
        vatNumber: [{ validator: inputValidators.vatNumber, trigger: 'blur' }],
        email: [{ validator: inputValidators.email, trigger: 'change' }],
        bankName: [{ validator: inputValidators.primitive, trigger: 'change' }],
        bankNumber: [{ validator: inputValidators.ibanNumber, trigger: 'blur' }],
        bankSwift: [{ validator: inputValidators.swiftNumber, trigger: 'blur' }],
      },
      companyId: '',
      processingUserDataRequest: false,
      serverResponse: null,
      router,
    };
  },

  watch: {
    company(newVal: unknown): void {
      if (Object.keys(newVal).length) {
        this.assignCompanyData(newVal);
      }
    },
  },

  mounted(): void {
    this.assignExistingDataWhileOnOnboarding();
  },

  methods: {
    submitForm(saveOption: 'saveAndQuit' | 'saveAndContinue'): void {
      helpers.validateAndSubmitUserData(this, 'onboardingCDForm', saveOption);
    },

    assignExistingDataWhileOnOnboarding(): void {
      if (this.isPartOfProfile) return;

      this.onboardingCD.country = store.state.userCountry || '';
      this.onboardingCD.email = store.state.userEmail || localStorage.getItem('email') || '';
      this.onboardingCD.phone = store.state.userCompanyPhoneNumber || '';
    },

    assignCompanyData(companyData: {
      name: string;
      addresses: {
        country: string;
        street: string;
        postal_code: string;
        house_number: string;
        apartment_number: string;
        city: string;
      }[];
      vat: string | string[];
      emails: string[];
      bank: { name: string; number: string; swift: string };
      registration_number: string;
      phones: string[];
      id: string;
    }): void {
      if (!this.isPartOfProfile) return;

      this.checkInputTriggers(companyData);

      this.onboardingCD.companyName = companyData.name;
      this.onboardingCD.street = companyData.addresses[0].street;
      this.onboardingCD.houseNumber = companyData.addresses[0].house_number;
      this.onboardingCD.apartmentNumber = companyData.addresses[0].apartment_number;
      this.onboardingCD.city = companyData.addresses[0].city;
      this.onboardingCD.postalCode = companyData.addresses[0].postal_code;
      this.onboardingCD.country = companyData.addresses[0].country;
      this.onboardingCD.vatNumber = companyData.vat;
      this.onboardingCD.email = companyData.emails[0];
      this.onboardingCD.bankName = companyData.bank.name;
      this.onboardingCD.bankNumber = companyData.bank.number;
      this.onboardingCD.bankSwift = companyData.bank.swift;
      this.onboardingCD.registrationNumber = companyData.registration_number;
      this.onboardingCD.phone = companyData.phones[0];
      this.companyId = companyData.id;
    },

    async submitUserData(): Promise<void> {
      this.serverResponse = {};
      this.processingUserDataRequest = true;

      const payload = {
        name: this.onboardingCD.companyName,
        vat: this.onboardingCD.vatNumber,
        registration_number: this.onboardingCD.registrationNumber,
        phones: [this.onboardingCD.phone],
        emails: [this.onboardingCD.email],
        addresses: [
          {
            street: this.onboardingCD.street,
            house_number: this.onboardingCD.houseNumber,
            apartment_number: this.onboardingCD.apartmentNumber,
            city: this.onboardingCD.city,
            postal_code: this.onboardingCD.postalCode,
            country: this.onboardingCD.country,
          },
        ],
        bank: {
          name: this.onboardingCD.bankName,
          number: this.onboardingCD.bankNumber,
          swift: this.onboardingCD.bankSwift,
        },
      };

      if (this.isPartOfProfile) await this.postOrPatchDataWhileOnProfile(payload);
      else await this.postDataWhileOnboarding(payload);

      this.processingUserDataRequest = false;
    },

    async postDataWhileOnboarding(payload: unknown): Promise<void> {
      const resp = await requestAPI({
        method: 'POST',
        url: '/api/v1/onboard/business-information',
        payload,
      });

      if (resp.ok) {
        helpers.endOnboarding();
        this.$emit('setStep', 'complete to home', resp);
      } else {
        const data = await passDataToResponseAlert(resp);
        this.serverResponse = data;
      }
    },

    async postOrPatchDataWhileOnProfile(payload: unknown): Promise<void> {
      let resp;

      if (this.companyId) {
        resp = await requestAPI({
          method: 'PATCH',
          contentType: 'application/merge-patch+json',
          url: `/api/v1/companies/${this.companyId}`,
          payload,
        });
      } else {
        resp = await requestAPI({
          method: 'POST',
          url: `/api/v1/companies`,
          payload,
        });
      }

      if (resp.ok) {
        const shouldDisplaySuccessMessage =
          !this.focusOnProgressCircle || store.state.lastVisitedProject;
        if (shouldDisplaySuccessMessage) this.$message.success(this.$t('businessInfoUpdated'));

        const makeImmediateRefreshTokenCall = true;
        const clearOldRefrTokenTimeout = true;

        refreshTokenHandler(makeImmediateRefreshTokenCall, clearOldRefrTokenTimeout);

        store.state.lastVisitedProject ? null : this.$emit('runGetProfileCompletionProcess');
        store.state.lastVisitedProject ? this.goBackToTheProject() : null;
      } else {
        const data = await passDataToResponseAlert(resp);
        this.serverResponse = data;
      }
    },

    goNextScreen(): void {
      this.$emit('setStep', 'complete to home');

      if (!localStorage.getItem('notifiedAboutIncompleteStage2')) {
        this.$notify.info({
          title: this.$t('info'),
          message: this.$t('skipStage4OfAssessorOnboardingInfo'),
          duration: 0,
          offset: 120,
        });
      } else localStorage.removeItem('notifiedAboutIncompleteStage2');
    },

    goBackToTheProject(): void {
      setTimeout(() => {
        store.commit('setLastVisitedProject', '');
      }, 1000);

      router.push({
        path: '/assessor-request-confirmation',
        query: {
          id: store.state.lastVisitedProject,
        },
      });
    },

    checkInputTriggers(companyData: Record<string, any>): void {
      this.validationRules.vatNumber[0].trigger = companyData.vat ? 'change' : 'blur';
      this.validationRules.bankNumber[0].trigger = companyData.bank.number ? 'change' : 'blur';
      this.validationRules.bankSwift[0].trigger = companyData.bank.swift ? 'change' : 'blur';
    },
  },
};
